import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { StatCard } from '../components/StatCard';
import { Button } from '../components/SubscriptionAppButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { SearchBar } from '../components/SearchBar';
import { TableFilter } from '../components/TableFilter';
import type { Filter } from '../components/TableFilter';
import { ExportButton } from '../components/ExportButton';
import { Plus, ArrowUpDown, ArrowUp, ArrowDown, Calendar, User, Link as LinkIcon, CheckCircle2, AlertCircle, Clock, XCircle, Archive, Eye, EyeOff } from 'lucide-react';
import { CreateTaskModal } from '../components/CreateTaskModal';
import type { TaskData, EditTaskData } from '../components/CreateTaskModal';
import { useTasks } from '../contexts/TaskContext';
export function TasksPage() {
  const navigate = useNavigate();
  const {
    tasks,
    addTask,
    updateTask
  } = useTasks();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});
  const [addedFilters, setAddedFilters] = useState<Set<string>>(new Set());
  const [openFilterDropdowns, setOpenFilterDropdowns] = useState<Set<string>>(new Set());
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<EditTaskData | null>(null);
  // Archive state
  const [archivedTaskIds, setArchivedTaskIds] = useState<Set<string>>(new Set());
  const [showArchived, setShowArchived] = useState(false);
  const handleArchiveTask = (taskId: string) => {
    setArchivedTaskIds(prev => new Set([...prev, taskId]));
  };
  const handleUnarchiveTask = (taskId: string) => {
    setArchivedTaskIds(prev => {
      const next = new Set(prev);
      next.delete(taskId);
      return next;
    });
  };
  const handleArchiveAllCompleted = () => {
    const completedIds = tasks.filter(t => t.status === 'Complete').map(t => t.id);
    setArchivedTaskIds(prev => new Set([...prev, ...completedIds]));
  };
  const completedCount = tasks.filter(t => t.status === 'Complete').length;
  const archivedCount = tasks.filter(t => archivedTaskIds.has(t.id)).length;
  // Calculate stats for Sarah Chen
  const myOpenTasks = tasks.filter(t => t.assignee.name === 'Sarah Chen' && t.status !== 'Complete' && t.status !== 'Cancelled').length;
  const overdue = tasks.filter(t => t.status !== 'Complete' && t.status !== 'Cancelled' && new Date(t.dueDate) < new Date('2026-02-12')).length;
  const dueToday = tasks.filter(t => t.status !== 'Complete' && t.status !== 'Cancelled' && new Date(t.dueDate).toDateString() === new Date('2026-02-12').toDateString()).length;
  const completedThisWeek = tasks.filter(t => t.status === 'Complete').length;
  const stats = {
    myOpenTasks,
    overdue,
    dueToday,
    completedThisWeek
  };
  // Filters configuration
  const availableFilters: Filter[] = [{
    id: 'status',
    label: 'Status',
    options: [{
      id: 'open',
      label: 'Open',
      value: 'Open'
    }, {
      id: 'in-progress',
      label: 'In Progress',
      value: 'In Progress'
    }, {
      id: 'complete',
      label: 'Complete',
      value: 'Complete'
    }, {
      id: 'cancelled',
      label: 'Cancelled',
      value: 'Cancelled'
    }],
    selectedValues: filterValues.status || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('status')
  }, {
    id: 'priority',
    label: 'Priority',
    options: [{
      id: 'low',
      label: 'Low',
      value: 'Low'
    }, {
      id: 'medium',
      label: 'Medium',
      value: 'Medium'
    }, {
      id: 'high',
      label: 'High',
      value: 'High'
    }, {
      id: 'urgent',
      label: 'Urgent',
      value: 'Urgent'
    }],
    selectedValues: filterValues.priority || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('priority')
  }, {
    id: 'assignee',
    label: 'Assignee',
    options: [{
      id: 'mj',
      label: 'Mike Johnson',
      value: 'Mike Johnson'
    }, {
      id: 'sc',
      label: 'Sarah Chen',
      value: 'Sarah Chen'
    }, {
      id: 'ct',
      label: 'Chris Taylor',
      value: 'Chris Taylor'
    }],
    selectedValues: filterValues.assignee || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('assignee')
  }, {
    id: 'dueDate',
    label: 'Due Date',
    options: [{
      id: 'overdue',
      label: 'Overdue',
      value: 'overdue'
    }, {
      id: 'today',
      label: 'Due Today',
      value: 'today'
    }, {
      id: 'week',
      label: 'Due This Week',
      value: 'week'
    }, {
      id: 'month',
      label: 'Due This Month',
      value: 'month'
    }],
    selectedValues: filterValues.dueDate || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('dueDate')
  }];
  const handleFilterChange = (filterId: string, value: string | string[]) => {
    setFilterValues(prev => ({
      ...prev,
      [filterId]: Array.isArray(value) ? value : [value]
    }));
  };
  const handleFilterRemove = (filterId: string) => {
    const newAddedFilters = new Set(addedFilters);
    newAddedFilters.delete(filterId);
    setAddedFilters(newAddedFilters);
    setFilterValues(prev => ({
      ...prev,
      [filterId]: []
    }));
  };
  const handleToggleFilterDropdown = (filterId: string) => {
    const newOpenFilterDropdowns = new Set(openFilterDropdowns);
    if (newOpenFilterDropdowns.has(filterId)) {
      newOpenFilterDropdowns.delete(filterId);
    } else {
      newOpenFilterDropdowns.add(filterId);
    }
    setOpenFilterDropdowns(newOpenFilterDropdowns);
  };
  const handleAddFilter = (filterId: string) => {
    const newAddedFilters = new Set(addedFilters);
    newAddedFilters.add(filterId);
    setAddedFilters(newAddedFilters);
  };
  const handleClearAll = () => {
    setFilterValues({});
    setAddedFilters(new Set());
    setSearchQuery('');
  };
  const handleSort = (key: string) => {
    setSortConfig(current => {
      if (current?.key === key) {
        return {
          key,
          direction: current.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return {
        key,
        direction: 'asc'
      };
    });
  };
  // Filter and Sort Logic
  const filteredTasks = useMemo(() => {
    let result = tasks.filter(task => {
      // Hide archived unless showArchived is on
      if (!showArchived && archivedTaskIds.has(task.id)) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!task.title.toLowerCase().includes(query) && !task.assignee.name.toLowerCase().includes(query) && !task.linkedTo?.id.toLowerCase().includes(query)) {
          return false;
        }
      }
      if (filterValues.status?.length && !filterValues.status.includes(task.status)) return false;
      if (filterValues.priority?.length && !filterValues.priority.includes(task.priority)) return false;
      if (filterValues.assignee?.length && !filterValues.assignee.includes(task.assignee.name)) return false;
      return true;
    });
    if (sortConfig) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.key as keyof typeof a];
        const bValue = b[sortConfig.key as keyof typeof b];
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [tasks, searchQuery, filterValues, sortConfig, showArchived, archivedTaskIds]);
  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'Urgent':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" data-id="element-13700" data-mp="1dqy8rxbr6xf8">
            Urgent
          </span>;
      case 'High':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800" data-id="element-13701" data-mp="hb3gqpok88dx">
            High
          </span>;
      case 'Medium':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-13702" data-mp="7dfhbmg0ecpf">
            Medium
          </span>;
      case 'Low':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-13703" data-mp="ca5yye7gpgcy">
            Low
          </span>;
      default:
        return null;
    }
  };
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Open':
        return 'info';
      case 'In Progress':
        return 'warning';
      case 'Complete':
        return 'success';
      case 'Cancelled':
        return 'default';
      default:
        return 'default';
    }
  };
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-13704" data-mp="3fsza6kcrpt3y">
      <div className="mb-6" data-id="element-13705" data-mp="3fusgfyjgybbf">
        <NavBar tabs={[]} title="Tasks" subtitle="Manage and track team tasks and follow-ups" data-id="element-13706" data-mp="3fqwb7atn0pn3" />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-13707" data-mp="33b4zsph6r6pe">
        <StatCard label="My Open Tasks" value={stats.myOpenTasks} data-id="element-13708" data-mp="3i7w3haxw74kt" />
        <StatCard label="Overdue" value={stats.overdue} trend={<span className="text-xs font-medium text-[#EF4444]" data-id="element-13710" data-mp="3d8jdwifmzjej">
              Needs Attention
            </span>} data-id="element-13709" data-mp="3i74e4huw232z" />
        <StatCard label="Due Today" value={stats.dueToday} data-id="element-13711" data-mp="2pk9uto9t59r3" />
        <StatCard label="Completed This Week" value={stats.completedThisWeek} data-id="element-13712" data-mp="2kkdt5vol1uep" />
      </div>

      <hr className="my-10 border-[#CBD5E1]" data-id="element-13713" data-mp="2z91jnnjkndxs" />

      {/* Actions Bar */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg mb-6 p-6 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-13714" data-mp="2zacrbyg515u6">
        <div className="flex flex-col gap-4" data-id="element-13715" data-mp="2uggsyuvypgyh">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-id="element-13716" data-mp="26rp9crwfu6xb">
            <div className="w-full lg:w-80 flex-shrink-0" data-id="element-13717" data-mp="21ufj5a185r8f">
              <SearchBar placeholder="Search tasks..." value={searchQuery} onChange={setSearchQuery} className="w-full" data-id="element-13718" data-mp="21rtbzbi8xdqq" />
            </div>

            <div className="flex items-center gap-3 flex-shrink-0 ml-auto" data-id="element-13719" data-mp="1ne34vigowxzy">
              {/* Show/Hide Archived Toggle */}
              <button onClick={() => setShowArchived(!showArchived)} className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-[13px] font-medium border transition-colors ${showArchived ? 'bg-[#F0F9FF] border-[#0EA5E9] text-[#0EA5E9]' : 'bg-white border-[#E2E8F0] text-[#64748B] hover:border-[#94A3B8] hover:text-[#475569]'}`} data-id="element-13720" data-mp="1icds9ydtkc9r">
                {showArchived ? <Eye className="w-4 h-4" data-id="element-13721" data-mp="zn7e8onz08ua" /> : <EyeOff className="w-4 h-4" data-id="element-13722" data-mp="zoihmndi7ekv" />}
                {showArchived ? `Showing archived (${archivedCount})` : `${archivedCount} archived`}
              </button>

              {/* Archive All Completed */}
              {completedCount > 0 && <button onClick={handleArchiveAllCompleted} className="flex items-center gap-1.5 px-3 py-2 rounded-md text-[13px] font-medium border border-[#E2E8F0] bg-white text-[#64748B] hover:border-[#94A3B8] hover:text-[#475569] transition-colors" data-id="element-13723" data-mp="prdt4gw6r70w">
                  <Archive className="w-4 h-4" data-id="element-13724" data-mp="fkbas2aoa0lq" />
                  Archive completed
                </button>}

              <ExportButton variant="tasks" onExport={async type => console.log(`Exporting ${type}`)} data-id="element-13725" data-mp="klqwlwkocb56" />

              <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyButton={true} data-id="element-13726" data-mp="pi7gufor65cz" />

              <Button variant="primary" iconLeft={<Plus className="w-4 h-4" data-id="element-13728" data-mp="1t678p46oo64" />} onClick={() => {
              setEditingTask(null);
              setIsCreateTaskModalOpen(true);
            }} data-id="element-13727" data-mp="1u7qvhz9mizg">
                Create Task
              </Button>
            </div>
          </div>

          {addedFilters.size > 0 && <div className="flex items-center gap-3" data-id="element-13729" data-mp="3a97yoqb2uuvq">
              <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyChips={true} data-id="element-13730" data-mp="3a7n5b40i1hzg" />
              <button onClick={handleClearAll} className="text-[13px] text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors" data-id="element-13731" data-mp="3bz4uwd6auxjl">
                Clear all
              </button>
            </div>}
        </div>
      </div>

      {/* Tasks Table */}
      <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-13732" data-mp="2jc0smfylcxbi">
        <div className="overflow-x-auto" data-id="element-13733" data-mp="2t83s1e226hur">
          <table className="w-full" data-id="element-13734" data-mp="2t9yhn5o9yy56">
            <thead className="bg-[#F1F5F9]" data-id="element-13735" data-mp="2o9aexmqoosv4">
              <tr data-id="element-13736" data-mp="2o897ds8vmoi5">
                <th className="px-4 py-3 w-12" data-id="element-13737" data-mp="2o7zcbq8z5r5l">
                  <span className="sr-only" data-id="element-13738" data-mp="8hmwsxgbx4wd">Complete</span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-24" data-id="element-13739" data-mp="3j1veupccf6c">
                  <button onClick={() => handleSort('priority')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-13740" data-mp="b21eez8w44p6">
                    Priority
                    {sortConfig?.key === 'priority' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-13741" data-mp="23h0sci6sl7t" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-13742" data-mp="637x0vqhftjq" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-13743" data-mp="63zru4c99n8y" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-13744" data-mp="tvep3ip6dedc">
                  <button onClick={() => handleSort('title')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-13745" data-mp="l0qo44qmuiuc">
                    Title
                    {sortConfig?.key === 'title' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-13746" data-mp="1crof94dcpvdp" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-13747" data-mp="1cq3gt3nx1sak" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-13748" data-mp="1hjogd055qubv" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-48" data-id="element-13749" data-mp="3q1acgj2a0en3">
                  <button onClick={() => handleSort('assignee')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-13750" data-mp="3l2z5qh7a2k93">
                    Assignee
                    {sortConfig?.key === 'assignee' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-13751" data-mp="clciyo68wqkk" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-13752" data-mp="cmxae4x9jte7" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-13753" data-mp="7lgbug294j0s" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-32" data-id="element-13754" data-mp="2tosaphijdk5">
                  <button onClick={() => handleSort('dueDate')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-13755" data-mp="3uwprhec0sgl3">
                    Due Date
                    {sortConfig?.key === 'dueDate' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-13756" data-mp="297ltp19vcidt" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-13757" data-mp="2996l4i0vzl7g" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-13758" data-mp="2e5x175coygsv" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-40" data-id="element-13759" data-mp="2i6z53zhjzh16">
                  Linked To
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-32" data-id="element-13760" data-mp="2s6g98ch5pmv1">
                  <button onClick={() => handleSort('status')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-13761" data-mp="2x49aqs7ilsoz">
                    Status
                    {sortConfig?.key === 'status' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-13762" data-mp="35wtda3qxghf3" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-13763" data-mp="3ax7lblsj3rd4" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-13764" data-mp="3aul4swnnu1ir" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-40" data-id="element-13765" data-mp="1ftj276yygw5y">
                  Created By
                </th>
                <th className="px-4 py-3 w-20" data-id="element-13766" data-mp="1aw9epwv4mukr">
                  <span className="sr-only" data-id="element-13767" data-mp="1pjujrwbkb3vb">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]" data-id="element-13768" data-mp="1krkwko1u0hwq">
              {filteredTasks.map(task => {
              const isCompleted = task.status === 'Complete';
              const isArchived = archivedTaskIds.has(task.id);
              return <tr key={task.id} className={`hover:bg-[#F8FAFC] transition-colors cursor-pointer ${isArchived ? 'bg-[#F8FAFC]' : ''}`} onClick={() => {
                setEditingTask({
                  id: task.id,
                  title: task.title,
                  assignee: task.assignee.name,
                  dueDate: task.dueDate,
                  priority: task.priority,
                  linkedTo: task.linkedTo
                });
                setIsCreateTaskModalOpen(true);
              }} data-id="element-13769" data-mp="1ui5t85xy3cd0">
                    {/* Checkbox */}
                    <td className="px-4 py-4" data-id="element-13770" data-mp="2prsk4vretrie">
                      <button onClick={e => {
                    e.stopPropagation();
                    if (isCompleted) {
                      updateTask(task.id, {
                        status: 'Open',
                        completedAt: undefined,
                        completedBy: undefined
                      });
                    } else {
                      updateTask(task.id, {
                        status: 'Complete',
                        completedAt: new Date().toISOString(),
                        completedBy: 'Sarah Chen'
                      });
                    }
                  }} className={`w-[18px] h-[18px] rounded border-2 flex items-center justify-center transition-colors ${isCompleted ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-[#CBD5E1] hover:border-[#94A3B8]'}`} data-id="element-13771" data-mp="2pqrbsz1s3j4d">
                        {isCompleted && <svg width="10" height="10" viewBox="0 0 10 10" fill="none" data-id="element-13772" data-mp="2f816c8tx1o3w">
                            <path d="M2 5L4.5 7.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-13773" data-mp="2a8ol1sip0p3f" />
                          </svg>}
                      </button>
                    </td>
                    <td className="px-4 py-4" data-id="element-13774" data-mp="20jesycnun6fb">
                      <span className={isCompleted ? 'opacity-50' : ''} data-id="element-13775" data-mp="20id7de97j1jc">
                        {getPriorityBadge(task.priority)}
                      </span>
                    </td>
                    <td className="px-4 py-4" data-id="element-13776" data-mp="1rgdo0fapzld8">
                      <span className={`text-sm font-medium transition-colors ${isCompleted ? 'text-[#94A3B8] line-through' : 'text-[#0F172A] hover:text-[#0EA5E9]'}`} data-id="element-13777" data-mp="1milvbj293v7z">
                        {task.title}
                      </span>
                    </td>
                    <td className="px-4 py-4" data-id="element-13778" data-mp="3gw3lzexyd878">
                      <div className={`flex items-center gap-2 ${isCompleted ? 'opacity-50' : ''}`} data-id="element-13779" data-mp="3bxa83ler7f6j">
                        <div className="w-6 h-6 rounded-full bg-[#E0F2FE] text-[#0EA5E9] flex items-center justify-center text-xs font-medium" data-id="element-13780" data-mp="3jhumfhsfyroa">
                          {task.assignee.avatar}
                        </div>
                        <span className="text-sm text-[#475569]" data-id="element-13781" data-mp="3t85rx8rrx8xa">
                          {task.assignee.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4" data-id="element-13782" data-mp="30ji8zjuhyvmw">
                      <div className={`flex items-center gap-1.5 ${isCompleted ? 'opacity-50' : ''}`} data-id="element-13783" data-mp="30ktcrjny03e0">
                        <Calendar className="w-3.5 h-3.5 text-[#94A3B8]" data-id="element-13784" data-mp="35jcybiymjd5u" />
                        <span className={`text-sm ${!isCompleted && new Date(task.dueDate) < new Date('2026-02-12') ? 'text-[#EF4444] font-medium' : 'text-[#475569]'}`} data-id="element-13785" data-mp="3afbl524b0n45">
                          {task.dueDate}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4" data-id="element-13786" data-mp="unxiv7la5pxd">
                      <span className={isCompleted ? 'opacity-50' : ''} data-id="element-13787" data-mp="2541xja8rnydc">
                        {task.linkedTo ? <button className="flex items-center gap-1.5 text-sm text-[#0EA5E9] hover:text-[#0284C7] hover:underline font-medium transition-colors" onClick={e => {
                      e.stopPropagation();
                      console.log('Navigate to', task.linkedTo?.id);
                    }} data-id="element-13788" data-mp="2a2wpp2smiza4">
                            <LinkIcon className="w-3.5 h-3.5" data-id="element-13789" data-mp="1m5hb3vhtlh61" />
                            {task.linkedTo.label || task.linkedTo.id}
                          </button> : <span className="text-sm text-[#94A3B8]" data-id="element-13790" data-mp="1r4arbvohrm9w">—</span>}
                      </span>
                    </td>
                    <td className="px-4 py-4" data-id="element-13791" data-mp="1v7yyid97ofum">
                      <Badge variant={getStatusBadgeVariant(task.status)} data-id="element-13792" data-mp="205s1yyj4qvr7">
                        {task.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-4" data-id="element-13793" data-mp="3bjm6qjchygrm">
                      <span className={`text-sm text-[#475569] ${isCompleted ? 'opacity-50' : ''}`} data-id="element-13794" data-mp="3bib0mcvkwwxc">
                        {task.createdBy}
                      </span>
                    </td>
                    {/* Archive/Unarchive action */}
                    <td className="px-4 py-4" data-id="element-13795" data-mp="3bxrf5g7336js">
                      {isCompleted && <button onClick={e => {
                    e.stopPropagation();
                    if (isArchived) {
                      handleUnarchiveTask(task.id);
                    } else {
                      handleArchiveTask(task.id);
                    }
                  }} className={`text-xs font-medium px-2 py-1 rounded transition-colors ${isArchived ? 'text-[#0EA5E9] hover:bg-[#F0F9FF]' : 'text-[#94A3B8] hover:text-[#64748B] hover:bg-[#F1F5F9]'}`} title={isArchived ? 'Unarchive' : 'Archive'} data-id="element-13796" data-mp="3bw6npzg2g3q5">
                          {isArchived ? 'Unarchive' : 'Archive'}
                        </button>}
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Empty state */}
        {filteredTasks.length === 0 && <div className="py-12 text-center" data-id="element-13797" data-mp="2gnl4d44ersxq">
            <p className="text-sm text-[#94A3B8]" data-id="element-13798" data-mp="2lfcoowwz12ff">No tasks found</p>
          </div>}
      </div>

      <CreateTaskModal isOpen={isCreateTaskModalOpen} onClose={() => {
      setIsCreateTaskModalOpen(false);
      setEditingTask(null);
    }} editTask={editingTask} onCreate={taskData => {
      addTask({
        ...taskData,
        status: 'Open',
        createdBy: 'Sarah Chen',
        linkedTo: taskData.linkedTo ? {
          id: taskData.linkedTo.id,
          type: taskData.linkedTo.type as any,
          label: taskData.linkedTo.label
        } : null,
        assignee: {
          name: taskData.assignee,
          avatar: taskData.assignee.split(' ').map(n => n[0]).join(''),
          email: `${taskData.assignee.toLowerCase().replace(' ', '.')}@acmewine.com`
        }
      });
      setIsCreateTaskModalOpen(false);
      setEditingTask(null);
    }} onUpdate={(taskId, taskData) => {
      // Convert the date back to the format used in context (e.g. "Feb 18 2026")
      const dueDateObj = new Date(taskData.dueDate + 'T00:00:00');
      const formattedDate = dueDateObj.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }).replace(',', '');
      updateTask(taskId, {
        title: taskData.title,
        priority: taskData.priority,
        dueDate: formattedDate,
        assignee: {
          name: taskData.assignee,
          avatar: taskData.assignee.split(' ').map(n => n[0]).join(''),
          email: `${taskData.assignee.toLowerCase().replace(' ', '.')}@acmewine.com`
        },
        linkedTo: taskData.linkedTo ? {
          id: taskData.linkedTo.id,
          type: taskData.linkedTo.type as any,
          label: taskData.linkedTo.label
        } : null
      });
      setIsCreateTaskModalOpen(false);
      setEditingTask(null);
    }} data-id="element-13799" data-mp="27ohmg3gz9jkt" />
    </div>;
}